import { useEffect, useMemo, useState } from 'react';
import { supabase } from './lib/supabase';
import MortgageReconciliation from './components/MortgageReconciliation';
import ActiveMortgageRemainders from './components/ActiveMortgageRemainders';
import SharedFixedExpenses from './components/SharedFixedExpenses';
import HouseholdContributions from './components/HouseholdContributions';
import './household.css';

type AppProps = { userId: string; userEmail: string; displayName: string };
type Section = 'overview' | 'mortgage' | 'fixed' | 'contributions';

type HouseholdSummary = {
  mortgageBalance: number;
  nextPaymentAmount: number;
  nextPaymentDate: string | null;
  acquisitionDebtBalance: number;
  principalPrepayments: number;
  remainderPending: number;
  mortgageAdvance: number;
  rentExpected: number;
  rentReceived: number;
  contributionsThisMonth: number;
  fixedExpected: number;
  fixedPaid: number;
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function monthBounds(reference = new Date()) {
  const year = reference.getUTCFullYear();
  const month = reference.getUTCMonth();
  return {
    start: new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(year, month + 1, 0)).toISOString().slice(0, 10),
  };
}

async function loadHouseholdSummary(): Promise<HouseholdSummary> {
  const today = new Date().toISOString().slice(0, 10);
  const { start, end } = monthBounds();
  const [mortgage, installment, debt, prepayments, remainders, rentalSources, rents, contributions, services, servicePayments] = await Promise.all([
    supabase.from('mortgage_accounts').select('current_projected_balance').limit(1).maybeSingle(),
    supabase.from('mortgage_installments_public').select('due_date, scheduled_total, actual_charge').gte('due_date', today).order('due_date', { ascending: true }).limit(1).maybeSingle(),
    supabase.from('acquisition_debts_public').select('current_balance').eq('status', 'active').limit(1).maybeSingle(),
    supabase.from('principal_prepayments_public').select('amount').eq('status', 'confirmed'),
    supabase.from('reimbursement_remainders_public').select('period_month, reimbursed_to_creditor, remainder_amount, settled_amount, status').neq('status', 'cancelled'),
    supabase.from('rental_sources').select('expected_amount').eq('is_active', true),
    supabase.from('rental_receipts').select('received_amount').neq('status', 'cancelled').gte('period_month', start).lte('period_month', end),
    supabase.from('household_contributions').select('amount').eq('record_status', 'active').gte('contributed_on', start).lte('contributed_on', end),
    supabase.from('shared_services').select('id, default_amount').eq('is_active', true),
    supabase.from('shared_service_payments').select('service_id, expected_amount, actual_amount, status').eq('period_month', start).neq('status', 'cancelled'),
  ]);

  const firstError = [mortgage.error, installment.error, debt.error, prepayments.error, remainders.error, rentalSources.error, rents.error, contributions.error, services.error, servicePayments.error].find(Boolean);
  if (firstError) throw firstError;

  const remainderRows = remainders.data ?? [];
  const paymentMap = new Map((servicePayments.data ?? []).map((payment) => [payment.service_id, payment]));
  const fixedExpected = (services.data ?? []).reduce((total, service) => {
    const payment = paymentMap.get(service.id);
    return total + Number(payment?.actual_amount ?? payment?.expected_amount ?? service.default_amount ?? 0);
  }, 0);
  const fixedPaid = (servicePayments.data ?? [])
    .filter((payment) => payment.status === 'paid')
    .reduce((total, payment) => total + Number(payment.actual_amount ?? payment.expected_amount ?? 0), 0);

  return {
    mortgageBalance: Number(mortgage.data?.current_projected_balance ?? 0),
    nextPaymentAmount: Number(installment.data?.actual_charge ?? installment.data?.scheduled_total ?? 0),
    nextPaymentDate: installment.data?.due_date ?? null,
    acquisitionDebtBalance: Number(debt.data?.current_balance ?? 0),
    principalPrepayments: (prepayments.data ?? []).reduce((total, row) => total + Number(row.amount ?? 0), 0),
    remainderPending: remainderRows
      .filter((row) => row.status !== 'settled')
      .reduce((total, row) => total + Number(row.remainder_amount ?? 0) - Number(row.settled_amount ?? 0), 0),
    mortgageAdvance: remainderRows
      .filter((row) => row.period_month >= start && row.period_month <= end)
      .reduce((total, row) => total + Number(row.reimbursed_to_creditor ?? 0), 0),
    rentExpected: (rentalSources.data ?? []).reduce((total, row) => total + Number(row.expected_amount ?? 0), 0),
    rentReceived: (rents.data ?? []).reduce((total, row) => total + Number(row.received_amount ?? 0), 0),
    contributionsThisMonth: (contributions.data ?? []).reduce((total, row) => total + Number(row.amount ?? 0), 0),
    fixedExpected,
    fixedPaid,
  };
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <article className="home-metric"><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function Overview({ onMortgage, onFixed, onContributions }: { onMortgage: () => void; onFixed: () => void; onContributions: () => void }) {
  const [summary, setSummary] = useState<HouseholdSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void loadHouseholdSummary()
      .then((data) => { if (active) setSummary(data); })
      .catch(() => { if (active) setError('No fue posible cargar el resumen financiero.'); });
    return () => { active = false; };
  }, []);

  const coverage = useMemo(() => {
    if (!summary?.nextPaymentAmount) return 0;
    const expectedResources = summary.mortgageAdvance + summary.rentExpected + summary.contributionsThisMonth;
    return Math.min(100, Math.round((expectedResources / summary.nextPaymentAmount) * 100));
  }, [summary]);

  if (error) return <section className="home-state home-state--error">{error}</section>;
  if (!summary) return <section className="home-state">Cargando información conciliada…</section>;

  const registeredResources = summary.mortgageAdvance + summary.rentReceived + summary.contributionsThisMonth;
  const expectedResources = summary.mortgageAdvance + summary.rentExpected + summary.contributionsThisMonth;
  const mortgageGap = Math.max(0, summary.nextPaymentAmount - expectedResources);
  const totalExpectedOutflow = summary.nextPaymentAmount + summary.fixedExpected;
  const totalExpectedResources = expectedResources;
  const totalGap = Math.max(0, totalExpectedOutflow - totalExpectedResources);

  return (
    <>
      <section className="home-heading home-heading--actions">
        <div>
          <p>Panel compartido · versión simplificada 0.14.1</p>
          <h1>La casa, con claridad.</h1>
          <span>Hipoteca, remanentes, servicios, rentas y aportaciones. Sin tarjetas ni estados personales.</span>
        </div>
        <div className="home-heading-buttons">
          <button type="button" onClick={onMortgage}>Hipoteca y remanentes</button>
          <button type="button" onClick={onFixed}>Gastos fijos</button>
          <button className="home-register-button" type="button" onClick={onContributions}>Aportaciones</button>
        </div>
      </section>

      <section className="home-hero">
        <div>
          <p>Próxima mensualidad hipotecaria</p>
          <h2>{currency.format(summary.nextPaymentAmount)}</h2>
          <span>{summary.nextPaymentDate ? `Programada para ${dateFormatter.format(new Date(`${summary.nextPaymentDate}T00:00:00Z`))}` : 'Pendiente de actualización bancaria'}</span>
          <div className="home-progress" aria-label={`${coverage}% de cobertura prevista`}><i style={{ width: `${coverage}%` }} /></div>
          <div className="home-hero-grid">
            <div><small>Anticipo aplicado</small><strong>{currency.format(summary.mortgageAdvance)}</strong></div>
            <div><small>Rentas esperadas</small><strong>{currency.format(summary.rentExpected)}</strong></div>
            <div><small>Faltante hipotecario</small><strong>{currency.format(mortgageGap)}</strong></div>
          </div>
        </div>
      </section>

      <section className="home-metrics">
        <Metric label="Saldo hipotecario" value={currency.format(summary.mortgageBalance)} detail="Última proyección bancaria" />
        <Metric label="Remanentes pendientes" value={currency.format(summary.remainderPending)} detail="Diferencias por liquidar con el acreedor" />
        <Metric label="Enganche + notaría" value={currency.format(summary.acquisitionDebtBalance)} detail="Deuda separada pendiente" />
        <Metric label="Abonos a capital" value={currency.format(summary.principalPrepayments)} detail="Acumulado confirmado" />
      </section>

      <section className="home-columns">
        <article className="home-panel">
          <div className="home-panel-heading"><div><small>Rentas</small><h2>Cobertura del periodo</h2></div><strong>{coverage}%</strong></div>
          <div className="home-row"><span>Esperado</span><strong>{currency.format(summary.rentExpected)}</strong></div>
          <div className="home-row"><span>Recibido</span><strong>{currency.format(summary.rentReceived)}</strong></div>
          <div className="home-row home-row--total"><span>Pendiente de depósito</span><strong>{currency.format(Math.max(0, summary.rentExpected - summary.rentReceived))}</strong></div>
        </article>

        <article className="home-panel">
          <div className="home-panel-heading"><div><small>Proyecto completo</small><h2>Hipoteca + servicios</h2></div></div>
          <div className="home-row"><span>Gastos fijos previstos</span><strong>{currency.format(summary.fixedExpected)}</strong></div>
          <div className="home-row"><span>Ya aplicado o recibido</span><strong>{currency.format(registeredResources)}</strong></div>
          <div className="home-row"><span>Obligaciones previstas</span><strong>{currency.format(totalExpectedOutflow)}</strong></div>
          <div className="home-row home-row--total"><span>Faltante total previsto</span><strong>{currency.format(totalGap)}</strong></div>
        </article>
      </section>
    </>
  );
}

export default function App({ userId, displayName }: AppProps) {
  const [section, setSection] = useState<Section>('overview');
  const navigation: Array<{ key: Section; label: string }> = [
    { key: 'overview', label: 'Resumen' },
    { key: 'mortgage', label: 'Hipoteca' },
    { key: 'fixed', label: 'Gastos fijos' },
    { key: 'contributions', label: 'Aportaciones' },
  ];

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <div className="home-app">
      <header className="home-header">
        <div className="home-brand"><span>FH</span><div><strong>Finanzas Hogar</strong><small>Sesión privada</small></div></div>
        <nav className="home-desktop-nav" aria-label="Navegación principal">
          {navigation.map((item) => <button key={item.key} className={section === item.key ? 'is-active' : ''} type="button" aria-current={section === item.key ? 'page' : undefined} onClick={() => setSection(item.key)}>{item.label}</button>)}
        </nav>
        <label className="home-mobile-nav"><span>Sección</span><select value={section} onChange={(event) => setSection(event.target.value as Section)}>{navigation.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label>
        <div className="home-user"><div className="home-user-copy"><span>{displayName}</span><small>Perfil del hogar</small></div><button className="home-signout" type="button" onClick={() => void signOut()}>Cerrar sesión</button></div>
      </header>

      <main className="home-main">
        {section === 'overview' && <Overview onMortgage={() => setSection('mortgage')} onFixed={() => setSection('fixed')} onContributions={() => setSection('contributions')} />}
        {section === 'mortgage' && (
          <>
            <MortgageReconciliation userId={userId} />
            <ActiveMortgageRemainders userId={userId} />
          </>
        )}
        {section === 'fixed' && <SharedFixedExpenses />}
        {section === 'contributions' && <HouseholdContributions userId={userId} />}
      </main>
    </div>
  );
}
